import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { doLogin, doLoginWithGoogle } from '@app/store/slices/authSlice';
import { getAuthConfig, AuthConfig } from '@app/api/auth.api';
import { takeSignOutNotice } from '@app/api/axios.api';
import * as S from './LoginForm.styles';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { Alert, Divider, Spin } from 'antd';
import loginShop from '@app/assets/logo.jpg';
import GoogleLoginButton from './GoogleLoginButton';
import { readUser } from '@app/services/localStorage.service';
import { sidebarNavigation } from '@app/components/layouts/main/sider/sidebarNavigation';
import { buildNavAccess, resolveLandingPath } from '@app/components/layouts/main/sider/navigationAccess';

interface LoginFormData {
  email: string;
  password: string;
}

export const initValues: LoginFormData = {
  email: '',
  password: '',
};

const DEFAULT_CONFIG: AuthConfig = {
  password_login_enabled: true,
  google_sso_enabled: false,
  google_client_id: '',
};

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isLoading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  // Lỗi hiển thị NGAY TRONG thẻ đăng nhập. Toast nổi che mất form và lặp lại thông báo
  // đã có dưới từng ô nhập, nên mọi lỗi đăng nhập đều nằm ở đây.
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Bị đăng xuất do phiên bị thu hồi / hết hạn → nói rõ lý do ngay trên form.
  useEffect(() => setNotice(takeSignOutNotice()), []);

  useEffect(() => {
    getAuthConfig()
      .then((cfg) => setConfig(cfg))
      .catch(() => setConfig(DEFAULT_CONFIG)); // lỗi cấu hình → giữ đăng nhập mật khẩu
  }, []);

  // Phản hồi đăng nhập đã kèm roles/permissions → vào thẳng trang được phép,
  // không đi qua `/` (tránh mount dashboard rồi bị 403).
  const goToLanding = useCallback(
    () => navigate(resolveLandingPath(sidebarNavigation, buildNavAccess(readUser())), { replace: true }),
    [navigate],
  );

  const handleSubmit = (values: LoginFormData) => {
    setLoading(true);
    setFormError(null);
    setNotice(null);
    dispatch(doLogin(values))
      .unwrap()
      .then(goToLanding)
      .catch((err) => {
        setFormError(err.message || 'Email hoặc mật khẩu không đúng.');
        setLoading(false);
      });
  };

  const handleGoogleCredential = useCallback(
    (idToken: string) => {
      setGoogleLoading(true);
      setFormError(null);
      setNotice(null);
      dispatch(doLoginWithGoogle(idToken))
        .unwrap()
        .then(goToLanding)
        .catch((err) => {
          setFormError(err.message || 'Đăng nhập Google thất bại. Vui lòng thử lại.');
          setGoogleLoading(false);
        });
    },
    [dispatch, goToLanding],
  );

  const cfg = config ?? DEFAULT_CONFIG;
  const showPassword = cfg.password_login_enabled;
  const showGoogle = cfg.google_sso_enabled && !!cfg.google_client_id;

  return (
    <div className="tw-w-full tw-max-w-[420px]">
      <div className="tw-rounded-2xl tw-bg-surface-container-lowest tw-p-8 tw-shadow-card sm:tw-p-10">
        <div className="tw-mb-7 tw-flex tw-flex-col tw-items-center tw-text-center">
          <img
            src={loginShop}
            alt="Hoa Chân Thật"
            className="tw-mb-4 tw-h-16 tw-w-16 tw-rounded-2xl tw-object-cover tw-shadow-card"
          />
          <h1 className="tw-m-0 tw-mb-1 tw-font-heading tw-text-2xl tw-font-bold tw-text-on-surface">Đăng nhập</h1>
          <p className="tw-m-0 tw-text-[13px] tw-text-on-surface-variant">
            Chào mừng trở lại! Đăng nhập để tiếp tục.
          </p>
        </div>

        {notice && (
          <Alert type="warning" showIcon message={notice} className="tw-mb-4" closable onClose={() => setNotice(null)} />
        )}
        {formError && (
          <Alert
            type="error"
            showIcon
            message={formError}
            className="tw-mb-4"
            closable
            onClose={() => setFormError(null)}
          />
        )}

        {!config ? (
          <div className="tw-py-6 tw-text-center">
            <Spin />
          </div>
        ) : (
          <>
            {showPassword && (
              <BaseForm
                layout="vertical"
                onFinish={handleSubmit}
                // Mặc định BaseForm bắn toast lặp lại đúng thông báo đã nằm dưới ô nhập.
                onFinishFailed={() => setFormError(null)}
                scrollToFirstError
                requiredMark="optional"
                initialValues={initValues}
              >
                <Auth.FormItem
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Vui lòng nhập email!' },
                    { type: 'email', message: 'Email không hợp lệ' },
                  ]}
                >
                  <Auth.FormInput placeholder="ban@vidu.com" />
                </Auth.FormItem>
                <Auth.FormItem
                  label="Mật khẩu"
                  name="password"
                  rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }]}
                >
                  <Auth.FormInputPassword placeholder="••••••••" />
                </Auth.FormItem>
                <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between">
                  <BaseForm.Item name="rememberMe" valuePropName="checked" noStyle>
                    <Auth.FormCheckbox>
                      <S.RememberMeText>Ghi nhớ tôi</S.RememberMeText>
                    </Auth.FormCheckbox>
                  </BaseForm.Item>
                  <Link to="/auth/forgot-password">
                    <S.ForgotPasswordText>Quên mật khẩu?</S.ForgotPasswordText>
                  </Link>
                </div>
                <BaseForm.Item noStyle>
                  <Auth.SubmitButton type="primary" htmlType="submit" loading={isLoading}>
                    Đăng nhập
                  </Auth.SubmitButton>
                </BaseForm.Item>
              </BaseForm>
            )}

            {showPassword && showGoogle && (
              <Divider plain style={{ margin: '20px 0 16px' }}>
                <span className="tw-text-xs tw-text-on-surface-variant">hoặc</span>
              </Divider>
            )}

            {showGoogle && (
              <Spin spinning={googleLoading}>
                <GoogleLoginButton clientId={cfg.google_client_id} onCredential={handleGoogleCredential} />
              </Spin>
            )}

            {!showPassword && !showGoogle && (
              <p className="tw-m-0 tw-text-center tw-text-[13px] tw-text-on-surface-variant">
                Chưa có phương thức đăng nhập nào được bật. Vui lòng liên hệ quản trị viên.
              </p>
            )}
          </>
        )}
      </div>

      <p className="tw-mb-0 tw-mt-5 tw-text-center tw-text-xs tw-text-on-surface-variant">
        © 2025 Hoa Chân Thật Portal. All rights reserved.
      </p>
    </div>
  );
};
